import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, ShoppingCart, Star, Heart } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Lookbook() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        fetch(`${API}/marketplace/items`)
            .then(r => r.ok ? r.json() : [])
            .then(all => {
                const merch = all.filter(i =>
                    i.category === 'merch' || i.category === 'streetwear' ||
                    i.subcategory === 'hoodies' || i.subcategory === 'tees' || i.subcategory === 'bottoms'
                );
                setItems(merch);
                if (merch.length > 0) setSelectedItem(merch[0]);
            })
            .finally(() => setLoading(false));
    }, []);

    const allImages = items.flatMap(i =>
        [i.image_url, ...(i.gallery_images || [])].filter(Boolean).map(url => ({ url, item: i }))
    );

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <Button size="sm" variant="ghost" onClick={() => navigate('/shop')} className="mb-2 text-muted-foreground">
                        <ArrowLeft size={16} className="mr-1" />Shop
                    </Button>
                    <span className="label-text text-hot-pink text-glow-pink">COLLECTION</span>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 font-display text-foreground" data-testid="lookbook-title">
                        Lookbook
                    </h1>
                    <p className="text-muted-foreground text-sm mt-2 max-w-md">
                        Premium streetwear designed for the electric riding culture. Every piece built to move.
                    </p>
                </motion.div>

                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="aspect-[3/4] rounded-xl" />)}
                    </div>
                ) : items.length === 0 ? (
                    <Card className="glass-card">
                        <CardContent className="p-8 text-center">
                            <p className="text-muted-foreground">No merch items in the catalog yet</p>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {/* Hero — Featured Item */}
                        {selectedItem && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                className="mb-8 relative rounded-2xl overflow-hidden cursor-pointer"
                                onClick={() => navigate(`/shop/item/${selectedItem.item_id}`)}>
                                <div className="aspect-[16/7] bg-muted relative">
                                    <img src={selectedItem.image_url} alt={selectedItem.name}
                                        className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                                        <Badge className="bg-hot-pink/90 text-white mb-2">
                                            <Star size={10} className="mr-1" weight="fill" />Featured
                                        </Badge>
                                        <h2 className="text-2xl sm:text-3xl font-display font-bold text-white">{selectedItem.name}</h2>
                                        <p className="text-sm text-white/70 mt-1 max-w-lg">{selectedItem.description}</p>
                                        <p className="font-mono text-neon-gold text-lg mt-2">{selectedItem.price_boltz?.toLocaleString()} Boltz</p>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* Gallery Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {allImages.map((img, idx) => (
                                <motion.div key={idx}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.05, duration: 0.3 }}
                                    className="group relative rounded-xl overflow-hidden cursor-pointer aspect-[3/4] bg-muted"
                                    onClick={() => navigate(`/shop/item/${img.item.item_id}`)}
                                    data-testid={`lookbook-img-${idx}`}>
                                    <img src={img.url} alt={img.item.name}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                                        <p className="text-white text-sm font-display font-bold truncate">{img.item.name}</p>
                                        <p className="text-neon-gold text-xs font-mono">{img.item.price_boltz?.toLocaleString()} Boltz</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Products List */}
                        <div className="mt-10">
                            <h3 className="font-display text-sm text-muted-foreground tracking-wider mb-4">FULL COLLECTION</h3>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {items.map(item => (
                                    <Card key={item.item_id} className="glass-card overflow-hidden cursor-pointer hover:border-primary/20 transition-all"
                                        onClick={() => navigate(`/shop/item/${item.item_id}`)}>
                                        <div className="aspect-square bg-muted relative">
                                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                            {item.stock === 0 && (
                                                <Badge className="absolute top-3 right-3 bg-plasma-red text-white">Sold Out</Badge>
                                            )}
                                        </div>
                                        <CardContent className="p-4">
                                            <p className="text-xs text-primary font-display">{item.brand}</p>
                                            <h4 className="font-bold text-foreground mt-0.5">{item.name}</h4>
                                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
                                            <div className="flex items-center justify-between mt-3">
                                                <p className="font-mono font-bold text-neon-gold">{item.price_boltz?.toLocaleString()} Boltz</p>
                                                <Button size="sm" className="btn-primary text-xs" data-testid={`lookbook-buy-${item.item_id}`}>
                                                    <ShoppingCart size={14} className="mr-1" />View
                                                </Button>
                                            </div>
                                            {item.specs?.colors?.length > 0 && (
                                                <p className="text-[10px] text-muted-foreground mt-2">{item.specs.colors.join(' · ')}</p>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}
